import React, { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationFunctionOptions } from '@apollo/client';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import { Formik, FormikProps, FormikValues } from 'formik';
import { ExecutionResult } from 'graphql';
import { Typography } from '@material-ui/core';
import { Classroom, Organization, UserClassroomEnrollment } from 'types/schema-generated-types';
import * as Yup from 'yup';
import ClassroomDetailsForm, { DetailsClassroomForm } from '../../components/ClassroomDetailsForm';

interface EditClassroomModalProps {
  updateClassroom: (
    options?: MutationFunctionOptions<any, Record<string, any>> | undefined
  ) => Promise<ExecutionResult<any>>;
  onClose: Function;
  organizations: Array<Organization>;
  classroom: Classroom;
}

const EditClassroomModal = ({
  onClose,
  classroom,
  organizations,
  updateClassroom
}: EditClassroomModalProps): ReactElement => {
  const { t } = useTranslation('EditClassroomModal');
  const [form, setForm] = useState<DetailsClassroomForm>({});
  const classroomId = classroom?.id;

  const closeModal = useCallback(
    (success?: boolean): void => {
      onClose(success);
    },
    [onClose]
  );

  const handleCancelClick = useCallback((): void => {
    closeModal();
    return;
  }, [closeModal]);

  const requiredFieldText = t('Field is required');

  const primaryEducator = React.useMemo(
    () =>
      classroom.users?.find((user: UserClassroomEnrollment) => {
        return user.classroomRoles.find(role => role.classification === 'EDUCATOR');
      }),
    [classroom]
  );
  const coeducators = React.useMemo(
    () =>
      classroom.users
        ?.filter(user => {
          return user.classroomRoles
            ? user.classroomRoles.find(role => role.classification === 'COEDUCATOR')
            : false;
        })
        .map(({ user: { id, firstName, lastName, username: email } }: UserClassroomEnrollment) => ({
          id,
          firstName,
          lastName,
          email
        })),
    [classroom]
  );
  const init = React.useMemo(
    () => ({
      name: classroom.name,
      organization: classroom.organizationId,
      educator: primaryEducator && primaryEducator.user ? primaryEducator.user.id : '',
      schoolYear: classroom.academicSessionId,
      secretCodeType: classroom.loginType,
      color: classroom.color,
      coeducators: coeducators || []
    }),
    [classroom, coeducators, primaryEducator]
  );

  const handleFormikSubmit = useCallback(
    async values => {
      setForm({
        ...form,
        ...values
      });

      const compareCoeducators = (): boolean => {
        const getId = ({ id }: { id: string | number }): number => +id;
        const initCoeducatorIds = init.coeducators?.map(getId).sort() || [];
        const currentCoeducatorIds = values.coeducators?.map(getId).sort() || [];
        if (initCoeducatorIds.length !== currentCoeducatorIds.length) {
          return false;
        }
        return initCoeducatorIds.every(function(element, index) {
          return element === currentCoeducatorIds[index];
        });
      };
      const diffEducator = +init.educator === +values.educator;

      const diffCoeducators = compareCoeducators();

      const userEnrollments = [
        { userId: +values.educator, classroomRole: 'EDUCATOR' },
        ...values.coeducators.map(({ id }: any) => ({
          userId: +id,
          classroomRole: 'COEDUCATOR'
        }))
      ];
      const enrollments = diffEducator && diffCoeducators ? {} : { userEnrollments };

      await updateClassroom({
        variables: {
          classroomId,
          data: {
            name: values.name,
            color: values.color,
            organizationId: values.organization,
            academicSessionId: values.schoolYear,
            loginType: values.secretCodeType,
            ...enrollments
          }
        }
      });

      closeModal(true);
    },
    [form, setForm, closeModal, classroomId, updateClassroom, init]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={init}
      validationSchema={Yup.object({
        name: Yup.string()
          .max(100)
          .required(requiredFieldText),
        organization: Yup.string().required(requiredFieldText),
        schoolYear: Yup.string().required(requiredFieldText),
        educator: Yup.string().required(requiredFieldText)
      })}
      validateOnChange={true}
      onSubmit={handleFormikSubmit}
    >
      {({ dirty, isSubmitting, isValid, submitForm }: FormikProps<FormikValues>): ReactElement => {
        return (
          <ModalContainer
            Header={
              <ModalHeader>
                <Typography variant="h2" component="h1">
                  {t('Edit Class Details')} - <b>{classroom.name}</b>
                </Typography>
              </ModalHeader>
            }
            Footer={
              <ModalFooter
                onCancel={handleCancelClick}
                onSubmit={submitForm}
                submitDisabled={!dirty || !isValid || isSubmitting}
              />
            }
          >
            <ModalContent>
              <ClassroomDetailsForm isEdit={true} organizations={organizations} />
            </ModalContent>
          </ModalContainer>
        );
      }}
    </Formik>
  );
};

export default EditClassroomModal;
